import { defineStore } from 'pinia';

import { authApi } from '@bv/api/auth';
import type { SuccessResponse } from '@bv/api/base';
import { useHostname, useCookies } from '@bv/composables';
import { getRouter } from '@bv/plugins/router';
import { useUserStore } from '@bv/stores';
import impersonateWidget from '@bv/utils/common/impersonate/widget';
import { goToConsole } from '@bv/utils/url/console/go-to';

const TOKEN_COOKIE_NAME = 'token';
const REFRESH_TOKEN_COOKIE_NAME = 'refresh_token';
const IMPERSONATE_TOKEN_COOKIE_NAME = 'impersonate_token';
const IMPERSONATE_TOKEN_EXPIRES_AT_COOKIE_NAME = 'impersonate_token_expires_at';

/**
 * Manages authentication tokens and session termination (logout, end impersonation).
 */
export const useAuthStore = defineStore('bvAuth', {
  state: () => ({
    token: null as string | null,
    refreshToken: null as string | null,
    _isImpersonationEnabled: import.meta.env.VITE_IMPERSONATE === 'true',
    _impersonateToken: null as string | null,
    _impersonateTokenExpiresAt: null as string | null,
  }),
  getters: {
    impersonateToken(state): string | null {
      return state._isImpersonationEnabled ? state._impersonateToken : null;
    },
    impersonateTokenExpiresAt(state): string | null {
      return state._isImpersonationEnabled ? state._impersonateTokenExpiresAt : null;
    },
  },
  actions: {
    /**
     * Persists the access token cookie and updates store state.
     */
    setToken(accessToken: string, expires: number): void {
      const { set, config } = useCookies();
      config();
      set(TOKEN_COOKIE_NAME, accessToken, { expires });
      this.token = accessToken;
    },

    /**
     * Removes the access token cookie and clears store state.
     */
    clearToken(): void {
      const { remove } = useCookies();
      remove(TOKEN_COOKIE_NAME);
      this.token = null;
    },

    /**
     * Persists the refresh token cookie and updates store state.
     */
    setRefreshToken(refreshToken: string, expires: number): void {
      const { set, config } = useCookies();
      config();
      set(REFRESH_TOKEN_COOKIE_NAME, refreshToken, { expires });
      this.refreshToken = refreshToken;
    },

    /**
     * Removes the refresh token cookie and clears store state.
     */
    clearRefreshToken(): void {
      const { remove } = useCookies();
      remove(REFRESH_TOKEN_COOKIE_NAME);
      this.refreshToken = null;
    },

    /**
     * Persists impersonate token cookies and updates store state.
     */
    setImpersonateTokens(accessToken: string, expiresAt: string | number, expires: number): void {
      const { set, config } = useCookies();
      const expiresAtValue = String(expiresAt);
      config();
      set(IMPERSONATE_TOKEN_COOKIE_NAME, accessToken, { expires });
      set(IMPERSONATE_TOKEN_EXPIRES_AT_COOKIE_NAME, expiresAtValue, { expires });
      this._impersonateToken = accessToken;
      this._impersonateTokenExpiresAt = expiresAtValue;
    },

    /**
     * Removes impersonate token cookies and clears store state.
     */
    clearImpersonateTokens(): void {
      const { remove } = useCookies();
      remove(IMPERSONATE_TOKEN_COOKIE_NAME);
      remove(IMPERSONATE_TOKEN_EXPIRES_AT_COOKIE_NAME);
      this._impersonateToken = null;
      this._impersonateTokenExpiresAt = null;
    },

    /**
     * Syncs auth-related cookies into store state without rewriting cookies.
     * Used during app initialization to hydrate Pinia from existing session cookies.
     */
    syncTokensFromCookies(): void {
      const { get, config } = useCookies();
      config();

      this.token = get<string>(TOKEN_COOKIE_NAME);
      this.refreshToken = get<string>(REFRESH_TOKEN_COOKIE_NAME);
      this._impersonateToken = get<string>(IMPERSONATE_TOKEN_COOKIE_NAME);
      this._impersonateTokenExpiresAt = get<string>(IMPERSONATE_TOKEN_EXPIRES_AT_COOKIE_NAME);
    },

    /**
     * Logs the user out, clears the token, and redirects to the login page.
     * This is intended for NORMAL (non-impersonate) sessions only.
     * For ending an impersonation session, use `endImpersonationSession()` instead.
     * @returns {Promise<SuccessResponse>}
     */
    async logout(): Promise<SuccessResponse> {
      return authApi.logout().finally(() => {
        const { domain, subdomain, isSuperAdmin } = useHostname();

        // Always remove the normal user tokens — this action is for normal users only.
        this.clearToken();
        this.clearRefreshToken();

        useUserStore().reset();

        const router = getRouter();
        if (router?.hasRoute('auth')) {
          router.push({ name: 'auth' });
          return;
        }

        if (isSuperAdmin) {
          // Redirect to the super admin login.
          window.location.href = `https://${subdomain}.${domain}/auth/login`;
        } else {
          // Redirect to the console login.
          goToConsole();
        }
      });
    },

    /**
     * Ends an impersonation session without making an API logout call.
     *
     * This is the correct method to call when an impersonation session ends — either
     * because the token has expired (via setTimeout) or the admin clicks "End Session".
     *
     * The key difference from `logout()` is that:
     * 1. No API request is made (the impersonate token may already be expired).
     * 2. Only the impersonate cookies are cleared, preserving the normal user session.
     * 3. The impersonate widget is destroyed.
     */
    endImpersonationSession(): void {
      const { domain } = useHostname();

      // Clear only the impersonate cookies — do NOT touch the normal user's token.
      this.clearImpersonateTokens();

      // Destroy the impersonate floating widget from the DOM.
      impersonateWidget.destroyWidget();

      // Reset user store state.
      useUserStore().reset();

      // Redirect to the super admin login.
      const envName = import.meta.env.VITE_ENV_NAME;
      if (envName === 'development') {
        window.location.href = `https://super-admin-gcp-dev.${domain}`;
      } else if (envName === 'staging') {
        window.location.href = `https://super-admin-gcp-stg.${domain}`;
      } else if (envName === 'preprod') {
        window.location.href = `https://super-admin-gcp-preprod.${domain}`;
      } else {
        window.location.href = `https://super-admin.${domain}`;
      }
    },
  },
});
